import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import Api from "../Api/Api.json";
import "../styles/rpmPatientsList.css";
import RpmListHeader from "../components/RpmListHeader";

const pageTitle = (
  <div className="flex flex-row gap-2 items-center">
    <svg
      width="26"
      height="24"
      viewBox="0 0 26 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M8.87936 22.8C9.21224 22.8 9.55304 22.798 9.90128 22.798H16.4934C16.8411 22.798 17.1819 22.8 17.5148 22.8C20.565 22.8 22.8956 22.644 22.8956 19.4553C22.8956 16.1179 20.3432 13.378 17.0835 13.0802C17.0576 14.2478 13.1984 17.1292 13.1984 17.1292C13.1984 17.1292 9.33824 14.248 9.31208 13.0804C6.05288 13.378 3.5 16.1179 3.5 19.4553C3.5 22.644 5.82968 22.8 8.87936 22.8ZM16.0777 18.4456C16.0777 18.3506 16.1634 18.274 16.2699 18.274H17.3125V17.232C17.3125 17.1264 17.3886 17.0397 17.4836 17.0397H18.5125C18.607 17.0397 18.6836 17.1256 18.6836 17.232V18.2743H19.7257C19.8313 18.2743 19.9177 18.3508 19.9177 18.4459V19.4743C19.9177 19.5693 19.832 19.6459 19.7257 19.6459H18.6834V20.688C18.6834 20.794 18.6066 20.88 18.5118 20.88H17.4834C17.3883 20.88 17.3118 20.7943 17.3118 20.688V19.6456H16.2697C16.1636 19.6456 16.0774 19.5691 16.0774 19.474L16.0777 18.4456Z"
        fill="#18357A"
        fillOpacity="1"
      />
      <path
        d="M13.1997 13.918C16.1478 13.918 18.5375 9.48499 18.5375 6.53707C18.5375 3.58963 16.1478 1.19995 13.1997 1.19995C10.252 1.19995 7.8623 3.58963 7.8623 6.53707C7.8623 9.48499 10.252 13.918 13.1997 13.918Z"
        fill="#18357A"
        fillOpacity="1"
      />
    </svg>
    <span>Clients</span>
  </div>
);

const fetchProviderClientList = async (id) => {
  const payload = {
    task: "getEnergybandProviderClientList",
    id,
  };
  console.log("getEnergybandProviderClientList request payload:", payload);
  const response = await fetch(Api.TELEDOC_PROVIDER_API, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

/** Map provider client-list row → same shape as OwnerLanding search `userData` for /owner/user-data */
function clientRowToUserData(patient) {
  const patientId = patient.patient_id ?? patient.patientId;
  const diId =
    patient.di_id ??
    patient.diId ??
    patientId ??
    patient.id;
  const id = patient.id ?? patientId ?? diId;
  return {
    ...patient,
    patient_id: patientId,
    name: patient.name ?? patient.full_name,
    di_id: diId,
    id,
  };
}

function normalizePhone(phone) {
  if (!phone) return "";
  let p = String(phone).trim();
  if (p.startsWith("0")) p = p.slice(1);
  return p;
}

function phoneFromSearchUser(searchResult, searchUserId) {
  if (searchResult?.error) return "";
  const raw =
    searchResult?.phone ??
    searchResult?.user_phone ??
    searchResult?.mobile ??
    searchUserId;
  return normalizePhone(raw);
}

const OwnerLanding2 = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ownerId = localStorage.getItem("energyband_owner_id");
  const [searchUserId, setSearchUserId] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  const handleOwnerLogout = () => {
    localStorage.removeItem("energyband_owner_info");
    localStorage.removeItem("energyband_owner_id");
    navigate("/owner");
  };

  const fetchUserByPhone = async (phone) => {
    const normalized = normalizePhone(phone);
    if (!normalized) return null;

    const headers = { headers: { "Content-Type": "application/json" } };
    const url = Api.TELEDOC_PROVIDER_API;
    const requestData = {
      task: "getPatientDetails",
      phone: normalized,
    };
    const response = await axios.post(url, requestData, headers);
    if (!response.data) {
      throw new Error("Network response was not ok");
    }
    return response.data[0] || null;
  };

  const postProviderTask = async (payload) => {
    const response = await axios.post(Api.TELEDOC_PROVIDER_API, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  };

  const handleAddUser = async () => {
    if (!ownerId) {
      alert("Owner session not found. Please log in again.");
      return;
    }
    if (!searchResult || searchResult.error) {
      alert("Search for a user first.");
      return;
    }
    const userPhone = phoneFromSearchUser(searchResult, searchUserId);
    if (!userPhone) {
      alert("No phone number available for this user.");
      return;
    }

    setIsAddingUser(true);
    try {
      const payload = {
        task: "assignEnergybandClientToProvider",
        provider_id: ownerId,
        phone: userPhone,
      };
      console.log("assignEnergybandClientToProvider payload:", payload);
      await postProviderTask(payload);
      await queryClient.invalidateQueries({
        queryKey: ["getEnergybandProviderClientList", ownerId],
      });
      alert("User added to your client list.");
    } catch (err) {
      console.error("Error adding user:", err);
      alert("Failed to add user. Please try again.");
    } finally {
      setIsAddingUser(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!searchResult || searchResult.error) {
      alert("Search for a user first.");
      return;
    }
    const userPhone = phoneFromSearchUser(searchResult, searchUserId);
    if (!userPhone) {
      alert("No phone number available for this user.");
      return;
    }

    setIsDeletingUser(true);
    try {
      const payload = {
        task: "removeEnergybandClientFromProvider",
        phone: userPhone,
      };
      console.log("removeEnergybandClientFromProvider payload:", payload);
      await postProviderTask(payload);
      await queryClient.invalidateQueries({
        queryKey: ["getEnergybandProviderClientList", ownerId],
      });
      alert("User removed from your client list.");
    } catch (err) {
      console.error("Error removing user:", err);
      alert("Failed to remove user. Please try again.");
    } finally {
      setIsDeletingUser(false);
    }
  };

  const handleSearchUser = async () => {
    if (!searchUserId.trim()) {
      alert("Please enter a user ID to search");
      return;
    }

    setIsSearching(true);
    setSearchResult(null);

    try {
      const userData = await fetchUserByPhone(searchUserId);
      if (userData) {
        setSearchResult(userData);
      } else {
        setSearchResult({ error: "No user found with this phone number" });
      }
    } catch (err) {
      console.error("Error searching for user:", err);
      setSearchResult({ error: "Error occurred while searching for user" });
    } finally {
      setIsSearching(false);
    }
  };

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["getEnergybandProviderClientList", ownerId],
    queryFn: () => fetchProviderClientList(ownerId),
    enabled: Boolean(ownerId),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });

  const patients = Array.isArray(data) ? data : [];

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <RpmListHeader
        pageTitle={pageTitle}
        ownerId={ownerId}
        onLogout={handleOwnerLogout}
      />
      {!ownerId ? (
        <div className="px-4 py-6 text-center text-[#5B6680]">
          Sign in as owner to load your client list.{" "}
          <Link to="/owner" className="text-[#4264D0] font-bold no-underline">
            Go to owner login
          </Link>
        </div>
      ) : (
        <div
          className="pb-4"
          style={{
            height: "calc(100vh - 113px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <div className="px-4 pt-4 pb-2">
            <div className="bg-blue-50 p-4 md:p-6 rounded-lg mb-4">
              <div className="text-xl font-semibold text-blue-800 mb-4">
                Search User
              </div>
              <div className="space-y-3">
                <input
                  type="number"
                  placeholder="Enter User ID"
                  value={searchUserId}
                  onChange={(e) => setSearchUserId(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                />
                <button
                  type="button"
                  onClick={handleSearchUser}
                  disabled={isSearching}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {isSearching ? "Searching..." : "Search User"}
                </button>
              </div>
            </div>

            {searchResult && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-md p-4 md:p-6 mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Search Result
                </h3>
                {searchResult.error ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="text-red-600 font-semibold">Error:</div>
                      <div className="text-red-700 ml-2">{searchResult.error}</div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-green-600 font-semibold mb-3">
                      User Found:
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {Object.entries(searchResult)
                        .filter(([key]) => key.toLowerCase() !== "id")
                        .map(([key, value]) => (
                          <div key={key} className="flex flex-col">
                            <span className="text-sm font-medium text-gray-600 capitalize">
                              {key.replace(/([A-Z])/g, " $1").trim()}:
                            </span>
                            <span className="text-gray-800 font-semibold">
                              {value !== null && value !== undefined
                                ? value.toString()
                                : "N/A"}
                            </span>
                          </div>
                        ))}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 mt-4">
                      <button
                        type="button"
                        onClick={handleAddUser}
                        disabled={isAddingUser || isDeletingUser}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex-1 disabled:bg-green-400 disabled:cursor-not-allowed"
                      >
                        {isAddingUser ? "Adding..." : "Add User"}
                      </button>
                      <button
                        type="button"
                        onClick={handleDeleteUser}
                        disabled={isAddingUser || isDeletingUser}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex-1 disabled:bg-red-400 disabled:cursor-not-allowed"
                      >
                        {isDeletingUser ? "Removing..." : "Delete User"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          navigate("/owner/user-data", {
                            state: { userData: searchResult },
                          });
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex-1"
                      >
                        View User Data
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {isPending ? (
            <div
              className="z-40 w-full flex justify-center items-center py-12"
              style={{ minHeight: "200px" }}
            >
              <div className="border-[6px] border-solid border-zinc-200 rounded-full border-t-[8px] border-t-[#3F4A9C] w-14 h-14 animate-spin" />
            </div>
          ) : isError ? (
            <div className="px-4 py-6 text-center text-red-600">
              {error?.message || "Could not load patient list."}
            </div>
          ) : (
            <div className="px-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {patients.map((patient) => {
                  const userData = clientRowToUserData(patient);
                  const rowKey =
                    patient.patient_id ??
                    patient.patientId ??
                    patient.di_id ??
                    patient.id ??
                    patient.phone;
                  return (
                    <div key={rowKey}>
                      <div className="bg-white rounded-lg p-3 cardPaitent">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <Link
                              to="/owner/user-data"
                              state={{ userData }}
                              className="no-underline"
                            >
                              {patient.imageSrc ? (
                                <img
                                  src={patient.imageSrc}
                                  alt=""
                                  className="rounded-full"
                                  style={{
                                    width: "50px",
                                    height: "50px",
                                  }}
                                />
                              ) : (
                                <div
                                  className="rounded-full text-white flex justify-center items-center"
                                  style={{
                                    width: "50px",
                                    height: "50px",
                                    fontSize: "24px",
                                    backgroundColor: "#3F4A9C",
                                  }}
                                >
                                  {userData.name?.[0] ?? "?"}
                                </div>
                              )}
                            </Link>
                          </div>
                          <div className="flex-grow ml-3">
                            <div className="flex justify-between">
                              <Link
                                to="/owner/user-data"
                                state={{ userData }}
                                className="no-underline"
                              >
                                <h4>{userData.name}</h4>
                              </Link>
                              <MoreHorizontal
                                className="text-[#5B6680] shrink-0"
                                size={18}
                                aria-hidden
                              />
                            </div>
                            <div className="grid grid-cols-3">
                              <div className="col-span-2">
                                <label>
                                  Patient No:{" "}
                                  <span>{userData.patient_id ?? "—"}</span>
                                </label>
                              </div>
                              <div>
                                <label>
                                  Age: <span>{userData.age}</span>
                                </label>
                              </div>
                              <div className="col-span-3">
                                <label>
                                  Phone: <span>{userData.phone}</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-12 mt-2">
                          <Link
                            to="/owner/user-data"
                            state={{ userData }}
                            className="text-decoration-none moreDetails"
                          >
                            See more
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OwnerLanding2;
